<script setup>
import {
  useBreakpoints,
  useDayjs,
  useGroupData,
} from '@daysmart/daysmart-vue-components/composables'
import { useCalendarListViewScrollspyState } from '../composables/useCalendarListViewScrollspyState'

const props = defineProps({
  events: {
    type: Object,
    required: true,
  },
  date: {
    type: String,
    required: true,
  },
  dateArray: {
    type: Array,
    required: true,
  },
  closedDates: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['selectDate'])

const groupKeys = computed(() =>
  props.dateArray?.map(date => date.format('YYYY-MM-DD')),
)
const { t, d } = useI18n()
const dayjs = useDayjs()

const { params, groupedData: groupedEvents } = useGroupData()
params.getGroupKey = event =>
  dayjs.value && dayjs.value(event.starts_at).format('YYYY-MM-DD')
params.initialGroupKeys = groupKeys
const scrollspyManager = ref(null)

watch(
  () => props.events,
  value => params.data = value,
  {
    immediate: true,
    deep: true,
  },
)

// update scrollspy manager state on breakpoint change or compact state change
const { current: currentBreakpoint } = useBreakpoints()
watch(
  () => [props.date, currentBreakpoint, groupedEvents],
  () => {
    // scrollspyManager.value.update()

    if (scrollspyManager.value)
      scrollspyManager.value.update()
    // nextTick(() => scrollspyManager.value.update())
  },
  { immediate: true, deep: true },
)

const { calendarListViewScrollspyState: scrollspyState }
  = useCalendarListViewScrollspyState(props.date)

watch(
  scrollspyState,
  (value) => {
    if (value !== props.date && groupKeys.value?.includes(value))
      emit('selectDate', value)
  },
  { immediate: true },
)

watch(
  () => props.date,
  (value) => {
    if (scrollspyState.value !== value)
      scrollspyState.value = value
  },
)
</script>

<template>
  <div class="schedule-list-view">
    <DSScrollspy.Manager
      ref="scrollspyManager"
      v-model="scrollspyState"
      :offset="270"
    >
      <div class="flex flex-col flex-gap-y-4">
        <template
          v-for="(group, index) in groupedEvents"
          :key="group.key"
        >
          <DSScrollspy.Anchor :name="group.key">
            <p
              class="border-1 border-surface-600 rounded-2 bg-surface-300 px-6 py-2.5 text-left text-text-500 font-600"
              :datetime="group.key"
            >
              {{ dayjs(group.key).format('dddd DD') }}
            </p>
          </DSScrollspy.Anchor>
          <ScheduleDayView
            v-if="closedDates.includes(group.key) || group.data.length > 0"
            :events="group.data"
            :closed-dates="closedDates"
            :date="group.key"
            class="mb-2"
          />
          <template v-else>
            <div class="empty-container">
              <div class="empty-event">
                {{ t('No Events') }}
              </div>
            </div>
          </template>
        </template>
      </div>
    </DSScrollspy.Manager>
  </div>
</template>

<style lang="scss" scoped>
.schedule-list-view {
  :deep(.calendar-event-container) {
    @apply p-0 flex-gap-0;

    &:first-child {
      @apply rounded-t-2;
      .caption {
        @apply rounded-t-2 border-t-0;
      }

      .calendar-event-item-header {
        @apply border-t-0 rounded-t-2;
      }
    }

    &:last-child {
      @apply rounded-b-2;
    }
  }

  .empty-container {
    @apply flex flex-1 justify-center items-center min-h-54px;
  }

  .empty-event {
    @apply text-sm bg-surface-300 rounded p-block-2 p-inline-4 text-text-400 text-center;
  }
}
</style>
